import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormGroupDefault,
  FormGroupSwitch,
  PageContent,
} from "beproduct-ui";
import { api } from "@/lib/api";
import { ScheduleCard } from "@/components/schedule-card";
import { PageHeader } from "@/components/navigation/page-header";
import { EnabledBadge } from "@/components/status-badge";

interface JobData {
  id: string;
  tenant_id: string;
  name: string;
  enabled: boolean;
  schedule_cron: string | null;
  schedule_tz: string;
  retry_limit: number;
  retry_delay_s: number;
  timeout_s: number;
}

interface FormState {
  name: string;
  enabled: boolean;
  schedule_cron: string;
  schedule_tz: string;
  retry_limit: number;
  retry_delay_s: number;
  timeout_s: number;
}

export const Route = createFileRoute("/_authed/$companySlug/jobs/$jobId/edit")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: EditJobPage,
});

function EditJobPage() {
  const { company } = Route.useRouteContext();
  const { companySlug, jobId } = Route.useParams();
  const qc = useQueryClient();

  const jobQ = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api<{ job: JobData }>(`/api/jobs/${jobId}`),
  });

  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (jobQ.data) {
      const j = jobQ.data.job;
      setForm({
        name: j.name,
        enabled: j.enabled,
        schedule_cron: j.schedule_cron ?? "",
        schedule_tz: j.schedule_tz ?? "UTC",
        retry_limit: j.retry_limit,
        retry_delay_s: j.retry_delay_s,
        timeout_s: j.timeout_s,
      });
    }
  }, [jobQ.data]);

  const m = useMutation({
    mutationFn: (data: FormState) =>
      api(`/api/jobs/${jobId}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...data,
          schedule_cron: data.schedule_cron.trim() || null,
        }),
      }),
    onSuccess: () => {
      toast.success("Saved. Scheduler picks up changes within ~30s.");
      qc.invalidateQueries({ queryKey: ["job", jobId] });
      qc.invalidateQueries({ queryKey: ["tenant", company.id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (jobQ.error) {
    return (
      <PageContent>
        <div className="p-8 text-destructive">Failed to load job.</div>
      </PageContent>
    );
  }
  if (!jobQ.data || !form) {
    return (
      <PageContent>
        <div className="p-8 text-muted-foreground">Loading…</div>
      </PageContent>
    );
  }

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  return (
    <PageContent>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate(form);
        }}
        className="pt-4 pb-8 md:pt-6 space-y-4 max-w-3xl"
      >
        <PageHeader title={`Edit workflow: ${jobQ.data.job.name}`} />

        <Card>
          <CardHeader><CardTitle>Identity</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <FormGroupDefault
              label="Name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
            <div className="flex items-center justify-between rounded-md border p-3 bg-muted/30">
              <div className="flex items-center gap-3">
                <FormGroupSwitch
                  label=""
                  checked={form.enabled}
                  onCheckedChange={(c) => update("enabled", c)}
                />
                <div>
                  <div className="font-medium text-sm">Workflow is enabled</div>
                  <p className="text-xs text-muted-foreground">
                    Disabled workflows don't fire on cron and skip the next reconcile.
                  </p>
                </div>
              </div>
              <EnabledBadge enabled={form.enabled} />
            </div>
          </CardContent>
        </Card>

        <ScheduleCard
          cron={form.schedule_cron}
          onCronChange={(c) => update("schedule_cron", c)}
          tz={form.schedule_tz}
          onTzChange={(t) => update("schedule_tz", t)}
        />

        <Card>
          <CardHeader><CardTitle>Reliability</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <FormGroupDefault
              label="Retry limit"
              type="number"
              min={0}
              value={form.retry_limit}
              onChange={(e) => update("retry_limit", Number(e.target.value))}
            />
            <FormGroupDefault
              label="Retry delay (sec)"
              type="number"
              min={0}
              value={form.retry_delay_s}
              onChange={(e) => update("retry_delay_s", Number(e.target.value))}
            />
            <FormGroupDefault
              label="Timeout (sec)"
              type="number"
              min={60}
              value={form.timeout_s}
              onChange={(e) => update("timeout_s", Number(e.target.value))}
            />
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={m.isPending}>
            {m.isPending ? "Saving…" : "Save"}
          </Button>
          <Button asChild variant="ghost">
            <Link to="/$companySlug" params={{ companySlug }}>Cancel</Link>
          </Button>

        </div>
      </form>
    </PageContent>
  );
}
